





































































































































import {
  Vue,
  Component,
  Prop,
} from "@/shared/vuepack";
import { Validator } from "vee-validate";
import InstrumentName from "../InstrumentName.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { formatCurrency } from "@/shared/currency";
import { useNotifierStore } from "@/shared/store/notifier";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";
import { useDrivewealthSweepStore } from "@/shared/store/drivewealthSweep";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import {
  DrivewealthSweep, SelectedInstrument, InstrumentStatus, DrivewealthAccountManagementType
} from "@/shared/types";

const ManagedStockSymbol = "RIAMANAGED";
const LeafhouseLogoUrl = "img/LH_Logo_Round_dark.png";

@Component({
  components: {
    InstrumentName,
    GenericLoadingSpinner,
  }
})
export default class ReivewAutomatedInvestmentFunding extends Vue {
  formatCurrency = formatCurrency;
  $validator: Validator;
  hypotheticalBalance: number = null;
  isSending = false;
  sweepWasActive = false;

  notifierStore = useNotifierStore();
  hsaDailyBalanceStore = useHsaDailyBalanceStore();
  drivewealthSweepStore = useDrivewealthSweepStore();
  custodianSettingsStore = useCustodianSettingsStore();

  @Prop({ required: false })
  isPreenrollment: boolean;

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  mounted () {
    this.hsaDailyBalanceStore.loadInstantBalance({ username: this.username, partnerId: this.partnerId });
    this.sweepWasActive = this.drivewealthSweepStore.sweepIsActive;
  }

  get portalFunctionalityDisabled (): boolean {
    return this.custodianSettingsStore.portalFunctionalityDisabled;
  }

  get isLoaded () {
    return !this.hsaDailyBalanceStore.getInstantBalance.loading;
  }

  get hsaBalance () {
    if (this.hypotheticalBalance !== null) {
      return this.hypotheticalBalance;
    }
    // else
    return this.hsaDailyBalanceStore.instantBalance.amount;
  }

  set hsaBalance (value: number) {
    this.hypotheticalBalance = value;
  }

  get amountInvested () {
    return Math.max(0, this.hsaBalance - this.drivewealthSweepStore.investmentThreshold);
  }

  get isAcceptDisabled (): boolean {
    if (this.portalFunctionalityDisabled) {
      return true;
    }

    return !!this.username || !!this.partnerId;
  }

  get showLowPurchaseAmountWarning () {
    return this.amountInvested > 0 && this.amountInvested < 10;
  }

  get instrumentWithLowestAllocation () {
    if (this.drivewealthSweepStore.selectedInstruments.length < 2) {
      return null;
    }
    let ret = this.drivewealthSweepStore.selectedInstruments[0];
    for (let i = 1; i < this.drivewealthSweepStore.selectedInstruments.length; i++) {
      if (this.drivewealthSweepStore.selectedInstruments[i].allocation <= ret.allocation) {
        ret = this.drivewealthSweepStore.selectedInstruments[i];
      }
    }

    return ret;
  }

  lowAllocationWarning (instrument: SelectedInstrument, index: number) {
    return {
      id: `lowAllocation-${index}`,
      shouldShow: instrument.allocation < 10 ||
        (this.amountInvested > 1
            && this.amountInvested < 10
            && this.instrumentWithLowestAllocation
            && instrument
            && instrument.instrumentId === this.instrumentWithLowestAllocation.instrumentId)
    };
  }

  onAccept (): void {
    if (this.portalFunctionalityDisabled) {
      return;
    }

    this.isSending = true;

    const sweep: DrivewealthSweep = {
      isActive: true,
      threshold: this.drivewealthSweepStore.investmentThreshold,
      isPreenrollment: this.isPreenrollment,
      funds: this.drivewealthSweepStore.selectedInstruments.map(i => ({
        symbol: i.stockSymbol,
        percentage: i.allocation,
        status: i.status,
        drivewealthAccountId: i.drivewealthAccountId,
        accountType: i.accountType
      }))
    };

    const apiCall = this.drivewealthSweepStore.postSweepV2;

    apiCall(sweep)
      .then(_ => {
        this.notifierStore.setSuccess(
          `Automatic investment funding is now ${this.sweepWasActive ? "updated" : "active"}.`);
        this.$emit("sweepAccepted");
      })
      .catch(_ => {
        this.isSending = false;
        this.notifierStore.setDanger(
          `Failed to ${this.sweepWasActive ? "update" : "turn on"} automatic investment funding.`);
      });
  }

  onEdit () {
    this.$emit("makeChanges");
  }

  purchaseAmount (instrument: SelectedInstrument) {
    return instrument.allocation / 100 * this.amountInvested;
  }

  allocationFields = [
    {
      key: "instrument",
      thClass: "text-left pl-2 col-md-6",
      tdClass: "pl-2 col-md-6",
      label: "Investment"
    },
    {
      key: "purchaseAllocation",
      label: "% Asset Allocation",
      thClass: "text-center col-md-3",
      tdClass: "col-md-3"
    },
    {
      key: "purchaseAmount",
      label: "Purchase",
      thClass: "text-center col-md-3",
      tdClass: "text-center col-md-3",
    },
    {
      key: "allocationWarning",
      label: "",
      tdClass: "text-center"
    },
  ];

  get allocationItems () {
    if (!this.drivewealthSweepStore.selectedInstruments) {
      return [];
    }

    return this.drivewealthSweepStore.selectedInstruments
      .filter(instrument => instrument.status === InstrumentStatus.Active)
      .map((instrument, index) => ({
        instrument: {
          symbol: instrument.stockSymbol,
          displaySymbol: this.isHsabManagedInstrument(instrument) ? "LeafHouse Managed" : null,
          companyName: this.isHsabManagedInstrument(instrument) ? "Managed Account" : instrument.companyName,
          website: instrument.website,
          imageUrl: this.isHsabManagedInstrument(instrument) ? LeafhouseLogoUrl : instrument.imageUrl
        },

        purchaseAllocation: instrument,
        purchaseAmount: formatCurrency(this.purchaseAmount(instrument)),
        allocationWarning: this.lowAllocationWarning(instrument, index),
      }));
  }

  isHsabManagedInstrument (instrument: SelectedInstrument): boolean {
    return instrument.stockSymbol === ManagedStockSymbol &&
      this.custodianSettingsStore.isHsaBankCustodian;
  }
}

